import { ExternalLink } from "lucide-react";
import {
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { usePopoverPosition } from "../../hooks/usePopoverPosition";

/** Every built-in string, so apps can translate them. */
export interface CitationMarkerLabels {
  /** The marker's accessible name. Keep the number in it: it's the marker's visible text. */
  marker: (number: number, title: string) => string;
  /** The link (or button, with `onOpen`) that opens the source. */
  open: string;
}

export const DEFAULT_CITATION_MARKER_LABELS: CitationMarkerLabels = {
  marker: (number) => `Source ${number}`,
  open: "Open",
};

export interface CitationMarkerProps {
  /** The number written in the text: `[3]` shows as a superscript 3. */
  number: number;
  /** The source's title; it names the popover. */
  title: string;
  /** Where the source lives, e.g. its site ("arxiv.org"). */
  site?: string;
  /** An excerpt, shown up to three lines. */
  snippet?: string;
  /** Open follows this link in a new tab. */
  href?: string;
  /** For sources opened inside the app: Open is a button that calls this instead of following `href`. */
  onOpen?: () => void;
  /** Overrides for the built-in strings (see DEFAULT_CITATION_MARKER_LABELS). */
  labels?: Partial<CitationMarkerLabels>;
}

type OpenedBy = "hover" | "focus" | "click";

/** Long enough for the pointer to cross from the marker into the popover. */
const CLOSE_DELAY_MS = 150;

const OPEN_ACTION_CLASS =
  "mt-2 inline-flex cursor-pointer items-center gap-1 rounded text-xs font-medium text-content-primary underline underline-offset-2 hover:text-content-secondary btn-focus-ring";

/**
 * An inline citation: a superscript number in running text that previews its source. Hover or keyboard focus shows
 * a preview; click, tap, Enter or Space pins it open and moves focus into it, so "Open" is one Tab away. Escape,
 * a click outside, or focus leaving closes it; focus returns to the marker. The popover is a non-modal dialog
 * named by the source's title.
 */
export function CitationMarker({ number, title, site, snippet, href, onOpen, labels }: Readonly<CitationMarkerProps>) {
  const l = { ...DEFAULT_CITATION_MARKER_LABELS, ...labels };
  const [openedBy, setOpenedBy] = useState<OpenedBy | null>(null);
  const markerRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Focus from a pointer press (the click decides) or focus handed back to the marker isn't a preview.
  const pressed = useRef(false);
  const quietFocus = useRef(false);
  const moveFocusIn = useRef(false);
  const panelId = useId();
  const titleId = useId();
  const isOpen = openedBy !== null;

  const { panelRef, style } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef: markerRef,
    placement: "bottom",
    align: "start",
    offset: 6,
  });

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);
  const close = useCallback(() => {
    cancelClose();
    setOpenedBy(null);
  }, [cancelClose]);
  const focusMarker = useCallback(() => {
    const marker = markerRef.current;
    if (!marker || document.activeElement === marker) return;
    quietFocus.current = true;
    marker.focus();
    quietFocus.current = false;
  }, []);

  useEffect(() => cancelClose, [cancelClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const hadFocus = panelRef.current?.contains(document.activeElement) ?? false;
      close();
      if (hadFocus) focusMarker();
    };
    const onPointerDown = (event: globalThis.PointerEvent) => {
      const target = event.target as Node;
      if (!markerRef.current?.contains(target) && !panelRef.current?.contains(target)) close();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [isOpen, close, focusMarker, panelRef]);

  // Pinned open: move focus in once the popover is positioned (a hidden element can't take focus).
  useLayoutEffect(() => {
    if (openedBy !== "click" || !moveFocusIn.current || style.visibility !== "visible") return;
    moveFocusIn.current = false;
    const panel = panelRef.current;
    (panel?.querySelector<HTMLElement>("a[href], button") ?? panel)?.focus();
  }, [openedBy, style, panelRef]);

  // Mouse only: touch fires emulated hover events before the tap, which would turn the tap into a toggle-off.
  const handlePointerEnter = (event: PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    cancelClose();
    setOpenedBy((by) => by ?? "hover");
  };
  const handlePointerLeave = (event: PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenedBy((by) => (by === "hover" ? null : by)), CLOSE_DELAY_MS);
  };
  const handleFocus = () => {
    const fromPointer = pressed.current;
    pressed.current = false;
    if (fromPointer || quietFocus.current) return;
    cancelClose();
    setOpenedBy((by) => by ?? "focus");
  };
  const handleBlur = (event: FocusEvent) => {
    pressed.current = false;
    const next = event.relatedTarget as Node | null;
    if (next && (markerRef.current?.contains(next) || panelRef.current?.contains(next))) return;
    close();
  };
  const handleClick = () => {
    pressed.current = false;
    cancelClose();
    if (openedBy === "click") {
      close();
      return;
    }
    moveFocusIn.current = true;
    setOpenedBy("click");
  };
  // The popover sits at the end of the page: leaving it hands focus back to the marker, and Tab carries on from there.
  const handlePanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;
    if (event.shiftKey) event.preventDefault();
    close();
    focusMarker();
  };

  let openAction = null;
  if (onOpen) {
    openAction = (
      <button
        type="button"
        className={OPEN_ACTION_CLASS}
        onClick={() => {
          close();
          onOpen();
        }}
      >
        {l.open}
      </button>
    );
  } else if (href) {
    openAction = (
      <a href={href} target="_blank" rel="noopener noreferrer" className={OPEN_ACTION_CLASS}>
        {l.open}
        <ExternalLink size={12} aria-hidden="true" className="shrink-0" />
      </a>
    );
  }

  return (
    <>
      <sup className="ml-0.5">
        <button
          ref={markerRef}
          type="button"
          aria-label={l.marker(number, title)}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={isOpen ? panelId : undefined}
          onPointerDown={() => {
            pressed.current = true;
          }}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={handleClick}
          className="relative inline-block min-w-[1.6em] cursor-pointer rounded-[0.35em] bg-surface-secondary px-[0.3em] py-[0.15em] text-center font-sans font-semibold not-italic leading-none text-content-primary no-underline transition-colors before:absolute before:-inset-1 hover:bg-surface-tertiary aria-expanded:bg-surface-tertiary btn-focus-ring"
        >
          {/* Hidden brackets: copied text keeps the citation as written ("[3]"), not a bare number. */}
          <span className="sr-only">[</span>
          {number}
          <span className="sr-only">]</span>
        </button>
      </sup>
      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-labelledby={titleId}
            tabIndex={-1}
            style={style}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
            onBlur={handleBlur}
            onKeyDown={handlePanelKeyDown}
            className="z-[110] w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-border-input bg-surface-elevated p-3 text-left shadow-lg outline-none"
          >
            {site && <p className="truncate text-xs text-content-tertiary">{site}</p>}
            <p id={titleId} className="line-clamp-2 text-sm font-medium text-content-primary">
              {title}
            </p>
            {snippet && <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-content-secondary">{snippet}</p>}
            {openAction}
          </div>,
          document.body,
        )}
    </>
  );
}
